import React, { useCallback, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Step,
    StepConnector,
    StepLabel,
    Stepper,
    Typography,
    withStyles
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { INameRecStatus, IIDAndName } from '../../models/Entity'

export type FranchiseeTarget = 'franchisee' | 'franchisee_alias'

export interface IDocumentDraftBase {
    name: string
    description: string
    startDate: string
    reviewDate: string
    versionNotes: string
    franchiseeTarget: FranchiseeTarget
    franchisees: INameRecStatus[]
    franchiseeAlias: IIDAndName | null
    hazardIds: Set<number>
    incidentTypeIds: Set<number>
    checklistTemplateIds: Set<number>
    ticketCategoryIds: Set<number>
    documentTypeIds: Set<number>
    file: File | null
}

export type DocumentModalMode = 'create' | 'edit' | 'renew'

export function useDocumentModalController<TDraft extends IDocumentDraftBase>({
    initialDraft,
    emptyDraft,
    onOpenChange
}: {
    initialDraft: () => TDraft
    emptyDraft: () => TDraft
    onOpenChange: (open: boolean) => void
}) {
    const [step, setStep] = useState(0)
    const [draft, setDraft] = useState<TDraft>(initialDraft)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const reset = useCallback(() => {
        setStep(0)
        setDraft(emptyDraft())
        setError(null)
    }, [emptyDraft])

    const close = useCallback(() => {
        onOpenChange(false)
        setTimeout(reset, 200)
    }, [onOpenChange, reset])

    function update<K extends keyof TDraft>(key: K, value: TDraft[K]) {
        setDraft((d) => ({ ...d, [key]: value }))
    }

    const canProceed = useMemo(() => {
        if (step === 0) {
            return (
                draft.name.trim().length > 0 &&
                draft.startDate.trim().length > 0 &&
                draft.reviewDate.trim().length > 0
            )
        }
        if (step === 1) {
            return draft.franchiseeTarget === 'franchisee_alias'
                ? draft.franchiseeAlias != null
                : draft.franchisees.length > 0
        }
        return true
    }, [
        step,
        draft.name,
        draft.startDate,
        draft.reviewDate,
        draft.franchiseeTarget,
        draft.franchisees,
        draft.franchiseeAlias
    ])

    return {
        step,
        setStep,
        draft,
        setDraft,
        submitting,
        setSubmitting,
        error,
        setError,
        close,
        update,
        canProceed
    }
}

const STEPPER_ACCENT = '#1F3A8A'
const STEPPER_LINE = '#D5DAE0'

const WizConnector = withStyles({
    alternativeLabel: {
        top: 14
    },
    line: {
        borderColor: STEPPER_LINE,
        borderTopWidth: 1.5
    },
    active: {
        '& $line': { borderColor: STEPPER_ACCENT }
    },
    completed: {
        '& $line': { borderColor: STEPPER_ACCENT }
    }
})(StepConnector)

const useStepIconStyles = makeStyles({
    root: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        fontSize: 13,
        fontWeight: 600,
        transition: 'all .2s',
        background: '#D5DAE0',
        color: '#363B43'
    },
    active: {
        background: STEPPER_ACCENT,
        color: '#fff'
    },
    completed: {
        background: STEPPER_ACCENT,
        color: '#fff'
    }
})

function WizStepIcon({
    active,
    completed,
    icon
}: {
    active?: boolean
    completed?: boolean
    icon: React.ReactNode
}) {
    const classes = useStepIconStyles()
    return (
        <Box
            className={`${classes.root}${active ? ` ${classes.active}` : ''}${
                completed ? ` ${classes.completed}` : ''
            }`}
        >
            {completed ? <CheckIcon style={{ fontSize: 16 }} /> : icon}
        </Box>
    )
}

const useShellStyles = makeStyles((theme: Theme) => ({
    titleBar: {
        background: theme.palette.primary.main,
        color: '#fff'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        paddingRight: theme.spacing(5)
    },
    titleText: { flex: 1, fontWeight: 700, fontSize: 20, color: '#fff' },
    stepCounter: {
        color: 'rgba(255,255,255,0.78)',
        fontSize: 12,
        letterSpacing: 0.4
    },
    closeBtn: {
        position: 'absolute',
        right: 12,
        top: 12,
        color: '#fff'
    },
    stepperBand: {
        padding: theme.spacing(2.5, 4),
        backgroundColor: theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    stepLabel: {
        '& .MuiStepLabel-label': {
            marginTop: theme.spacing(1),
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.text.secondary
        },
        '& .MuiStepLabel-label.MuiStepLabel-active': {
            color: theme.palette.text.primary,
            fontWeight: 600
        },
        '& .MuiStepLabel-label.MuiStepLabel-completed': {
            color: theme.palette.text.primary,
            fontWeight: 500
        }
    },
    stepLabelClickable: { cursor: 'pointer' },
    footer: {
        padding: theme.spacing(1.75, 3),
        justifyContent: 'space-between'
    },
    footerRight: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center'
    },
    cancelBtn: {
        color: theme.palette.text.secondary
    }
}))

export function DocumentModalShell({
    open,
    title,
    steps,
    step,
    setStep,
    submitting,
    loading,
    error,
    canProceed,
    submitLabel,
    onClose,
    onSubmit,
    children,
    className,
    titleClassName,
    stepperClassName,
    contentClassName,
    loadingClassName,
    errorClassName
}: {
    open: boolean
    title: string
    steps: readonly string[]
    step: number
    setStep: React.Dispatch<React.SetStateAction<number>>
    submitting: boolean
    loading: boolean
    error: string | null
    canProceed: boolean
    submitLabel: string
    onClose: () => void
    onSubmit: () => void
    children: React.ReactNode
    className?: string
    titleClassName?: string
    stepperClassName?: string
    contentClassName?: string
    loadingClassName?: string
    errorClassName?: string
}) {
    const shellStyles = useShellStyles()
    return (
        <Dialog
            open={open}
            // Clicking the backdrop must not discard wizard progress — the
            // modal only closes via the X button (or Cancel).
            disableBackdropClick
            onClose={() => !submitting && onClose()}
            className={className}
            maxWidth='md'
            fullWidth
            PaperProps={{ style: { borderRadius: 12 } }}
        >
            <DialogTitle disableTypography className={shellStyles.titleBar}>
                <Box
                    className={`${shellStyles.titleRow}${
                        titleClassName ? ` ${titleClassName}` : ''
                    }`}
                >
                    <Typography className={shellStyles.titleText}>
                        {title}
                    </Typography>
                    <Typography
                        component='span'
                        className={shellStyles.stepCounter}
                    >
                        Step {step + 1} of {steps.length}
                    </Typography>
                </Box>
                <IconButton
                    size='small'
                    onClick={onClose}
                    disabled={submitting}
                    className={shellStyles.closeBtn}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box
                className={`${shellStyles.stepperBand}${
                    stepperClassName ? ` ${stepperClassName}` : ''
                }`}
            >
                <Stepper
                    activeStep={step}
                    alternativeLabel
                    connector={<WizConnector />}
                    style={{ padding: 0, background: 'transparent' }}
                >
                    {steps.map((label, idx) => {
                        const clickable = idx < step
                        return (
                            <Step key={label} completed={idx < step}>
                                <StepLabel
                                    StepIconComponent={WizStepIcon}
                                    onClick={
                                        clickable
                                            ? () => setStep(idx)
                                            : undefined
                                    }
                                    className={`${shellStyles.stepLabel}${
                                        clickable
                                            ? ` ${shellStyles.stepLabelClickable}`
                                            : ''
                                    }`}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        )
                    })}
                </Stepper>
            </Box>

            <DialogContent className={contentClassName}>
                {loading ? (
                    <Box className={loadingClassName}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    children
                )}
                {error && <Box className={errorClassName}>{error}</Box>}
            </DialogContent>

            <Divider />
            <DialogActions className={shellStyles.footer}>
                <Button
                    onClick={onClose}
                    disabled={submitting}
                    className={shellStyles.cancelBtn}
                >
                    Cancel
                </Button>
                <Box className={shellStyles.footerRight}>
                    {step > 0 && (
                        <Button
                            variant='outlined'
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            disabled={submitting}
                        >
                            Back
                        </Button>
                    )}
                    {step < steps.length - 1 ? (
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => setStep((s) => s + 1)}
                            disabled={!canProceed || submitting}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={onSubmit}
                            disabled={!canProceed || submitting}
                        >
                            {submitLabel}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    )
}
